import { AxiosResponse } from 'axios';

import { AddOrganisationEstimate } from '@futrli-sdk/billing';
import {
  APIClients,
  API_PATHS,
  IntegrationsAPIClient,
  interpolatePathParams,
  REQUEST_METHODS,
  ROUTE_PARAMS
} from '@futrli/shared-services';
import { BillingModel } from '@futrli-sdk/app-store';
import { clearWorkspacesListingQueryCache } from '@futrli/views/operations/workspaces/queries/workspacesListingQuery';
import { Organisation, ProductSetup } from '@futrli-sdk/organisations';

import { ProviderConnection, ProviderRoutePayload } from './types';

/**
 * Gets the data from an AxiosResponse.
 * @param {getAxiosResponseData<T>} response - Gets an axios response and return the data.
 * @returns {T} - Response data.
 */
const getAxiosResponseData = <T>({ data }: AxiosResponse<T>): T => data;

/**
 * Activates a product in an organisation
 *
 * @param {string} workspaceId
 * @param {string} organisationId
 * @param {string} productId
 * @param {BillingModel} billingModel
 * @returns {Promise<ProductSetup>}
 */
export const activateProduct = async (
  workspaceId: string,
  organisationId: string,
  productId: string,
  billingModel: BillingModel
): Promise<ProductSetup> => {
  const url: string = interpolatePathParams(API_PATHS.PRODUCT_SETUP, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId
  });
  const response = await APIClients.getAPIClient().makeRequest<{ data: ProductSetup }>({
    payload: {
      request: {
        url,
        method: REQUEST_METHODS.PUT,
        data: {
          product_id: productId,
          billing_model: billingModel
        }
      }
    }
  });

  return response.data.data;
};

/**
 * Removes product setup from an organisation
 *
 * @param {string} workspaceId
 * @param {string} organisationId
 * @param {string} productSetupId
 * @returns {Promise<void>}
 */
export const deleteProductSetupById = async (workspaceId: string, organisationId: string, productSetupId: string) => {
  const url: string = interpolatePathParams(API_PATHS.PRODUCT_SETUP_BY_ID, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.PRODUCT_SETUP_ID]: productSetupId
  });
  return APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        url,
        method: REQUEST_METHODS.DELETE
      }
    }
  });
};

/**
 * Removes own access from an invited organisation.
 * @param {string} workspaceId
 * @param {string} organisationId
 * @returns {Promise<Organisation>}
 */
export const revokeOwnOrganisationAccess = async (workspaceId: string, organisationId: string) => {
  const interpolated: string = interpolatePathParams(API_PATHS.REMOVE_OWN_ORGANISATION_ACCESS, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId
  });

  return await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        method: REQUEST_METHODS.POST,
        url: interpolated
      }
    }
  });
};

/**
 * Creates an organisation.
 */
export const createOrg = async (workspaceId: string, orgData: Partial<Organisation>) => {
  const interpolated: string = interpolatePathParams(API_PATHS.ORGANISATIONS, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  const response = await APIClients.getAPIClient().makeRequest<{ data: Organisation }>({
    payload: {
      request: {
        method: REQUEST_METHODS.POST,
        url: interpolated,
        data: orgData
      }
    }
  });

  clearWorkspacesListingQueryCache();

  return response.data.data;
};

export const createProviderConnection = async (
  workspaceId: string,
  organisationId: string,
  payload: ProviderRoutePayload
): Promise<ProviderConnection> => {
  const interpolated: string = interpolatePathParams(API_PATHS.OAUTH2_CONNECT, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId
  });

  const response = await IntegrationsAPIClient.getAPIClient().makeRequest({
    payload: {
      request: {
        method: REQUEST_METHODS.POST,
        url: interpolated,
        data: payload
      }
    }
  });

  return getAxiosResponseData(response);
};

export const syncNowOauth2Connection = async (workspaceId: string, organisationId: string, dataSourceId: string) => {
  const interpolated: string = interpolatePathParams(API_PATHS.OAUTH2_SYNC_NOW, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.DATASOURCE_ID]: dataSourceId
  });

  const response = await IntegrationsAPIClient.getAPIClient().makeRequest({
    payload: {
      request: {
        method: REQUEST_METHODS.PUT,
        url: interpolated
      }
    }
  });

  return getAxiosResponseData(response);
};

/**
 * Fetches and returns add organisation estimates
 *
 * @function
 * @name fetchEstimates
 * @param {string} workspaceId - Workspace id.
 * @returns {Promise<AddOrganisationEstimate>} - Add organisation estimates object
 */
export const fetchEstimates = async (workspaceId: string): Promise<AddOrganisationEstimate> => {
  const path = interpolatePathParams(API_PATHS.ADD_ORGANISATION_ESTIMATES, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        url: path
      }
    }
  });

  return getAxiosResponseData(response);
};

/**
 * Fetches a list of organisations for a workspace.
 *
 * @param {string} workspaceId
 * @returns {Promise<Organisation[]>}
 */
export const fetchOrganisations = async (workspaceId: string): Promise<Organisation[]> => {
  const path = interpolatePathParams(API_PATHS.ORGANISATIONS, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        url: path
      }
    }
  });
  return getAxiosResponseData(response.data);
};
